import { useEffect, useState } from 'react'
import { Plus, Trash2, X, Loader2, RefreshCw, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { calendarApi } from '../api/services.js'
import toast from 'react-hot-toast'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const EVENT_COLORS = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-400', 'bg-red-400', 'bg-pink-400']

// Must match backend enum exactly (case-sensitive)
const EVENT_TYPES = ['holiday', 'exam', 'event', 'ptm', 'announcement']
const TYPE_LABELS = { holiday: 'Holiday', exam: 'Exam', event: 'Event', ptm: 'PTM', announcement: 'Announcement' }

const emptyForm = { title: '', date: '', endDate: '', type: 'event', description: '' }

export default function CalendarPage() {
  const today = new Date()
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const data = await calendarApi.getEvents({ month: month + 1, year })
      setEvents(Array.isArray(data) ? data : data?.events ?? [])
    } catch { toast.error('Failed to load events'); setEvents([]) }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchEvents() }, [month, year])

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Event title required'); return }
    if (!form.date) { toast.error('Event date required'); return }
    try {
      setSaving(true)
      const created = await calendarApi.createEvent(form)
      setEvents((prev) => [...prev, created])
      toast.success('Event added')
      setModalOpen(false)
      setForm(emptyForm)
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to add event') }
    finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    try {
      await calendarApi.removeEvent(id)
      setEvents((prev) => prev.filter((e) => (e._id || e.id) !== id))
      toast.success('Event removed')
      setDeleteId(null)
    } catch { toast.error('Failed to delete') }
  }

  // Build calendar grid
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const eventsOnDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return events.filter((e) => {
      const eDate = (e.date || e.startDate || '').slice(0, 10)
      return eDate === dateStr
    })
  }

  const isToday = (day) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear()

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">School Calendar</h2>
          <p className="text-sm text-gray-500 mt-0.5">{events.length} events this month</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button onClick={fetchEvents} className="btn-secondary p-2"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => { setForm(emptyForm); setModalOpen(true) }} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Event
          </button>
        </div>
      </div>

      {/* Month navigation */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-500" />
          </button>
          <h3 className="font-bold text-gray-900 text-lg">{MONTHS[month]} {year}</h3>
          <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronRight className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {DAYS_SHORT.map((d) => (
            <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        {loading ? (
          <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} />
              const dayEvents = eventsOnDay(day)
              return (
                <div
                  key={day}
                  className={`min-h-[60px] rounded-lg p-1 border transition-colors cursor-pointer hover:border-blue-200 ${isToday(day) ? 'bg-blue-50 border-blue-200' : 'border-transparent hover:bg-gray-50'}`}
                  onClick={() => { setForm({ ...emptyForm, date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` }); setModalOpen(true) }}
                >
                  <p className={`text-xs font-semibold text-center mb-1 ${isToday(day) ? 'text-blue-600' : 'text-gray-700'}`}>{day}</p>
                  {dayEvents.slice(0, 2).map((e, i) => (
                    <div key={e._id || e.id || i} className={`text-white text-[10px] font-medium rounded px-1 py-0.5 truncate mb-0.5 ${EVENT_COLORS[i % EVENT_COLORS.length]}`}>
                      {e.title}
                    </div>
                  ))}
                  {dayEvents.length > 2 && <p className="text-[10px] text-gray-400">+{dayEvents.length - 2} more</p>}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Events list */}
      {events.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm">Events This Month</h3>
          <div className="space-y-2">
            {events.map((e, i) => (
              <div key={e._id || e.id || i} className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 group transition-colors">
                <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${EVENT_COLORS[i % EVENT_COLORS.length]}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{e.title}</p>
                  <p className="text-xs text-gray-400">
                    {e.date ? new Date(e.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                    {e.type ? ` · ${TYPE_LABELS[e.type] || e.type}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => setDeleteId(e._id || e.id)}
                  className="p-1 rounded text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Add Event</h3>
              <button onClick={() => setModalOpen(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Event Title *</label>
                <input className="input-field w-full" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Diwali Holiday" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Start Date *</label>
                  <input type="date" className="input-field w-full" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">End Date</label>
                  <input type="date" className="input-field w-full" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Event Type</label>
                <select className="input-field w-full" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                <textarea className="input-field w-full h-16 resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional details" />
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 className="w-5 h-5 text-red-500" /></div>
            <h3 className="font-bold text-gray-900 mb-1">Remove Event?</h3>
            <p className="text-sm text-gray-500 mb-5">This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
